// src/components/YouTubeSummarizer.jsx
import React, { useState, useEffect } from 'react';
import { 
  Play, Pause, SkipForward, SkipBack, Download, ExternalLink, 
  Clock, Zap, Search, ChevronLeft, TrendingUp 
} from 'lucide-react';

const YouTubeSummarizer = () => {
  const [currentView, setCurrentView] = useState('home');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoId, setVideoId] = useState('');
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processingStep, setProcessingStep] = useState('');
  
  // Player state
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [player, setPlayer] = useState(null);

  // Search & Collections state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [recentVideos, setRecentVideos] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Update this to your actual Worker URL
  const API_BASE_URL = 'https://youtube-summarizer.nigelcannings.workers.dev';

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      
      window.onYouTubeIframeAPIReady = () => {
        console.log('YouTube API ready');
      };
    }
  }, []);

  // Load initial data
  useEffect(() => {
    loadCategories();
    loadRecentVideos();
  }, []);

  const extractVideoId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const loadYouTubePlayer = (videoId) => {
    if (window.YT && window.YT.Player) {
      const newPlayer = new window.YT.Player('youtube-player', {
        height: '390',
        width: '640',
        videoId: videoId,
        playerVars: {
          autoplay: 0,
          controls: 1,
          modestbranding: 1,
          rel: 0,
        },
        events: {
          onReady: (event) => {
            setPlayer(event.target);
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              startTimeUpdater(event.target);
            } else {
              setIsPlaying(false);
            }
          }
        }
      });
    }
  };

  const startTimeUpdater = (ytPlayer) => {
    const updateTime = () => {
      if (ytPlayer && ytPlayer.getCurrentTime) {
        setCurrentTime(ytPlayer.getCurrentTime());
        if (isPlaying) {
          setTimeout(updateTime, 1000);
        }
      }
    };
    updateTime();
  };

  const processVideo = async () => {
    if (!youtubeUrl) {
      setError('Please enter a YouTube URL');
      return;
    }

    const extractedVideoId = extractVideoId(youtubeUrl);
    if (!extractedVideoId) {
      setError('Invalid YouTube URL');
      return;
    }

    setLoading(true);
    setError('');
    setVideoId(extractedVideoId);
    setProcessingStep('Processing video...');

    try {
      const response = await fetch(`${API_BASE_URL}/api/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          videoId: extractedVideoId,
          targetRatio: 0.3
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Processing failed');
      }

      const data = await response.json();
      setVideoData(data);
      loadYouTubePlayer(extractedVideoId);
      setCurrentView('process');
      setProcessingStep('Complete! Ready to watch key moments.');
      
      loadRecentVideos();
    } catch (err) {
      setError('Failed to process video: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadRecentVideos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/collection`);
      if (response.ok) {
        const data = await response.json();
        setRecentVideos(data.videos || []);
      }
    } catch (error) {
      console.error('Failed to load recent videos:', error);
    }
  };

  const searchVideos = async () => {
    if (!searchQuery.trim() && !selectedCategory) return;

    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.append('q', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await fetch(`${API_BASE_URL}/api/search?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
        setCurrentView('search');
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const loadVideo = async (videoId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/video-details?videoId=${videoId}`);
      if (response.ok) {
        const data = await response.json();
        setVideoData(data);
        setVideoId(videoId);
        setYoutubeUrl(`https://www.youtube.com/watch?v=${videoId}`);
        loadYouTubePlayer(videoId);
        setCurrentView('process');
      }
    } catch (error) {
      console.error('Failed to load video:', error);
    }
  };

  const jumpToSegment = (index) => {
    if (player && videoData?.keySegments[index]) {
      player.seekTo(videoData.keySegments[index].start);
      setCurrentSegmentIndex(index);
      if (!isPlaying) {
        player.playVideo();
      }
    }
  };

  const playPause = () => {
    if (player) {
      if (isPlaying) {
        player.pauseVideo();
      } else {
        player.playVideo();
      }
    }
  };

  const nextSegment = () => {
    const nextIndex = Math.min(currentSegmentIndex + 1, (videoData?.keySegments.length || 1) - 1);
    jumpToSegment(nextIndex);
  };

  const previousSegment = () => {
    const prevIndex = Math.max(currentSegmentIndex - 1, 0);
    jumpToSegment(prevIndex);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const exportSummary = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId })
      });

      if (response.ok) {
        const summaryData = await response.json();
        const blob = new Blob([JSON.stringify(summaryData, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `youtube-summary-${videoId}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const shareVideo = () => {
    if (navigator.share && videoData) {
      navigator.share({
        title: videoData.videoInfo.title,
        text: videoData.completeSummary.overview,
        url: `https://youtube.com/watch?v=${videoId}`
      });
    } else {
      navigator.clipboard.writeText(`https://youtube.com/watch?v=${videoId}`);
    }
  };

  // Simple VideoCard component (inline to avoid import issues)
  const VideoCard = ({ video, onClick }) => (
    <div 
      className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 cursor-pointer hover:bg-white/20 transition-all transform hover:scale-105"
      onClick={() => onClick(video.videoId)}
    >
      <div className="aspect-video bg-black rounded-lg overflow-hidden mb-3">
        {video.thumbnail ? (
          <img 
            src={video.thumbnail} 
            alt={video.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
        )}
      </div>
      <h3 className="text-white font-semibold text-sm mb-2 line-clamp-2">
        {video.title}
      </h3>
      <p className="text-blue-200 text-xs mb-2">{video.channel}</p>
      <div className="flex justify-between items-center text-xs">
        <span className="text-blue-300">
          {formatDuration(video.duration)}
        </span>
        <span className="text-green-400">
          {formatDuration(video.condensedDuration)} condensed
        </span>
      </div>
      {video.summary && (
        <p className="text-blue-100 text-xs mt-2 line-clamp-2">
          {video.summary}
        </p>
      )}
    </div>
  );

  // Navigation component
  const Navigation = () => (
    <div className="flex gap-4">
      <button
        onClick={() => setCurrentView('home')}
        className={`px-6 py-3 rounded-xl font-semibold transition-all ${
          currentView === 'home'
            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
            : 'bg-white/10 text-blue-200 hover:bg-white/20'
        }`}
      >
        Home
      </button>
      <button
        onClick={() => setCurrentView('collections')}
        className={`px-6 py-3 rounded-xl font-semibold transition-all ${
          currentView === 'collections'
            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
            : 'bg-white/10 text-blue-200 hover:bg-white/20'
        }`}
      >
        Collections
      </button>
    </div>
  );

  // Home view
  const HomeView = () => (
    <div>
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 mb-8 border border-white/20">
        <h2 className="text-2xl font-bold text-white mb-6">Process New Video</h2>
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="Enter YouTube URL (e.g., https://www.youtube.com/watch?v=...)"
            className="flex-1 px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            disabled={loading}
          />
          <button
            onClick={processVideo}
            disabled={loading}
            className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Summarize
              </>
            )}
          </button>
        </div>

        {loading && (
          <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-blue-100">{processingStep}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-400/30 rounded-xl p-4 text-red-100">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 mb-8 border border-white/20">
        <h2 className="text-2xl font-bold text-white mb-6">Search Processed Videos</h2>
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, content, or topic..."
            className="flex-1 px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            onKeyPress={(e) => e.key === 'Enter' && searchVideos()}
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id} className="text-black">
                {cat.name} ({cat.count})
              </option>
            ))}
          </select>
          <button
            onClick={searchVideos}
            disabled={searchLoading}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {searchLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Search className="w-4 h-4" />
            )}
            Search
          </button>
        </div>
      </div>

      {recentVideos.length > 0 && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <TrendingUp className="text-green-400" />
            Recently Processed
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {recentVideos.slice(0, 8).map(video => (
              <VideoCard
                key={video.videoId}
                video={video}
                onClick={loadVideo}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Process view
  const ProcessView = () => {
    if (!videoData) return null;

    return (
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Smart Player</h2>
            <button
              onClick={() => setCurrentView('home')}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg text-blue-200 hover:bg-white/30 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          </div>
          
          <div className="aspect-video bg-black rounded-xl overflow-hidden mb-6">
            <div id="youtube-player"></div>
          </div>

          <div className="flex items-center justify-center gap-4 mb-6">
            <button
              onClick={previousSegment}
              className="p-3 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
            >
              <SkipBack className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={playPause}
              className="p-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full hover:from-blue-600 hover:to-purple-700 transition-all"
            >
              {isPlaying ? (
                <Pause className="w-8 h-8 text-white" />
              ) : (
                <Play className="w-8 h-8 text-white" />
              )}
            </button>
            <button
              onClick={nextSegment}
              className="p-3 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
            >
              <SkipForward className="w-6 h-6 text-white" />
            </button>
          </div>

          <div className="text-center text-blue-100 mb-4">
            <p>Segment {currentSegmentIndex + 1} of {videoData.keySegments?.length || 0}</p>
            <p className="text-sm opacity-75">
              {formatTime(currentTime)} / {formatTime(videoData.stats?.originalDuration || 0)}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 p-4 bg-white/10 rounded-xl mb-6">
            <div className="text-center">
              <div className="text-lg font-bold text-white">
                {formatDuration(videoData.stats?.originalDuration || 0)}
              </div>
              <div className="text-blue-200 text-sm">Original</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-400">
                {formatDuration(videoData.stats?.condensedDuration || 0)}
              </div>
              <div className="text-blue-200 text-sm">Key Moments</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-yellow-400">
                {Math.round((1 - (videoData.stats?.compressionRatio || 0)) * 100)}%
              </div>
              <div className="text-blue-200 text-sm">Time Saved</div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={exportSummary}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <a
              href={`https://youtube.com/watch?v=${videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <h1 className="text-2xl font-bold text-white mb-4">
              {videoData.videoInfo?.title || 'Video Title'}
            </h1>
            <p className="text-blue-200 mb-4">{videoData.videoInfo?.channelTitle || 'Channel'}</p>
            
            {videoData.completeSummary && (
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Summary</h3>
                <p className="text-blue-100">{videoData.completeSummary.overview}</p>
              </div>
            )}
          </div>

          {videoData.keySegments && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-6">Key Moments</h2>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {videoData.keySegments.map((segment, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-xl cursor-pointer transition-all ${
                      currentSegmentIndex === index
                        ? 'bg-blue-500/30 border-2 border-blue-400'
                        : 'bg-white/10 hover:bg-white/20 border border-white/20'
                    }`}
                    onClick={() => jumpToSegment(index)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-blue-300 font-semibold flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {formatTime(segment.start)}
                      </span>
                      <span className="text-yellow-400 text-sm">
                        {segment.importance}/10
                      </span>
                    </div>
                    <p className="text-white leading-relaxed">
                      {segment.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-800 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 mb-8 border border-white/20">
          <h1 className="text-4xl font-bold text-white mb-4 flex items-center gap-3">
            <Zap className="text-yellow-400" />
            YouTube Smart Summarizer
          </h1>
          <p className="text-blue-100 text-lg mb-6">
            AI-powered video summarization with smart collections and search
          </p>
          <Navigation />
        </div>

        {currentView === 'home' && <HomeView />}
        {currentView === 'process' && <ProcessView />}
      </div>
    </div>
  );
};

export default YouTubeSummarizer;